import { Button, Card, CardContent } from '@sea-snag/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { OnboardingWizard } from '../components/onboarding';
import { SeaSnagLogo } from '../components/sea-snag-logo';
import { authApi } from '../lib/api-client';

const onboardingSearchSchema = z.object({
  token: z.string(),
  mode: z.enum(['new', 'new-org']).optional(),
});

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
  validateSearch: onboardingSearchSchema,
});

function OnboardingPage() {
  const { token, mode: urlMode } = Route.useSearch();
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'valid'; email: string; mode: 'new' | 'new-org' }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    authApi
      .validateOnboardingToken(token)
      .then((data) => {
        setState({
          status: 'valid',
          email: data.email,
          // URL mode takes precedence (for existing user clicking "Create New Org" link)
          mode: urlMode ?? data.mode,
        });
      })
      .catch(() => {
        setState({
          status: 'error',
          message: 'This link is invalid or has expired.',
        });
      });
  }, [token, urlMode]);

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Validating your link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md animate-reveal-up">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="mb-2 text-xl font-bold">Link expired</h2>
            <p className="mb-6 text-sm text-muted-foreground">{state.message}</p>
            <Button asChild>
              <Link to="/get-started">Request a new link</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-lg py-8">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 animate-reveal-up">
            <SeaSnagLogo className="mx-auto size-12" color />
          </div>
          <h1 className="animate-reveal-up text-3xl font-bold">
            {state.mode === 'new' ? 'Set up your account' : 'Create a new organization'}
          </h1>
          <p className="animate-reveal-up reveal-delay-1 mt-2 text-muted-foreground">
            {state.mode === 'new'
              ? "Just a few steps and you'll be managing your fleet"
              : 'Set up your new workspace in just a moment'}
          </p>
        </div>
        <div className="animate-reveal-up reveal-delay-2">
          <OnboardingWizard email={state.email} token={token} mode={state.mode} />
        </div>
      </div>
    </div>
  );
}
